// Analytics: the detailed view behind the dashboard's 30-day summary. Pick a
// period (7 days to a year: stats are deleted after a year) and a widget, then
// see headline numbers, activity over time, the drop-off funnel and, for all
// widgets, the per-widget breakdown. With both heat pump and solar components, a
// switch narrows everything to one product (ProductSwitch). Below: leads by type, component types,
// contact form topics and the homes asking, then (QuoteFlow only) quote analytics.
import { useEffect, useMemo, useState } from "react";
import { useQuotesApi } from "../quotes/api";
import { useApi, type BillingOverview, type StatsRange, type Widget } from "../api";
import { Funnel, KpiRow, RANGES, TrendChart, WidgetTable, rangeLabel, useStats } from "../components/analytics";
import { LeadInsights, QuoteInsights } from "../components/insights";
import { ProductSwitch, inProduct, useProductFilter } from "../components/ProductSwitch";

export function AnalyticsPage() {
  const api = useApi();
  const [range, setRange] = useState<StatsRange>("30d");
  const [widgetId, setWidgetId] = useState("");
  const [widgets, setWidgets] = useState<Widget[]>([]);
  // All / ASHP / Solar: only offered when the subscription (Stripe) has both products
  const [billing, setBilling] = useState<BillingOverview | null>(null);
  useEffect(() => {
    api
      .getBilling()
      .then(setBilling)
      .catch(() => undefined);
  }, [api]);
  const { available: showSwitch, product, setProduct } = useProductFilter(billing);
  const { stats, error } = useStats(range, widgetId, product);
  const period = rangeLabel(range);
  // Contact forms stay out of the ASHP Estimator journey (no postcode or price steps)
  const contactIds = useMemo(() => new Set(widgets.filter((w) => w.branding?.layout?.style === "contact").map((w) => w.widgetId)), [widgets]);
  // QuoteFlow companies see the last step split into callbacks and detailed quotes
  const quotesApi = useQuotesApi();
  const [quoteFlow, setQuoteFlow] = useState(false);
  useEffect(() => {
    quotesApi
      .getAccess()
      .then((a) => setQuoteFlow(a.entitled))
      .catch(() => undefined);
  }, [quotesApi]);

  // Widget names for the filter and the per-widget table
  useEffect(() => {
    api.listWidgets().then(setWidgets).catch(() => undefined);
  }, [api]);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Analytics</h1>
        <p>How visitors use your website components and how far they get through each step. Figures are kept for one year.</p>
      </div>

      {/* Filters: one row above everything they control */}
      <div className="dash-filters">
        <div className="ed-segmented" role="group" aria-label="Period">
          {RANGES.map((r) => (
            <button key={r.value} type="button" className={r.value === range ? "is-on" : ""} aria-pressed={r.value === range} onClick={() => setRange(r.value)}>
              {r.label}
            </button>
          ))}
        </div>
        {showSwitch && (
          <ProductSwitch
            value={product}
            onChange={(p) => {
              setProduct(p);
              // A component of another product can't stay picked
              const picked = widgets.find((w) => w.widgetId === widgetId);
              if (picked && !inProduct(picked, p)) setWidgetId("");
            }}
          />
        )}
        <select className="input dash-widget-select" value={widgetId} onChange={(e) => setWidgetId(e.target.value)} aria-label="Component">
          <option value="">All components</option>
          {widgets.filter((w) => inProduct(w, product)).map((w) => (
            <option key={w.widgetId} value={w.widgetId}>
              {w.name}
            </option>
          ))}
        </select>
      </div>
      {error && <div className="flash flash-danger">{error}</div>}

      <KpiRow stats={stats} period={period} product={product} />
      {stats && <TrendChart stats={stats} />}
      {stats && <Funnel stats={stats} period={period} exclude={contactIds} quoteFlow={quoteFlow && product !== "solar"} product={product} />}
      {stats && !widgetId && <WidgetTable stats={stats} widgets={widgets} />}

      {/* Leads by type, component types, contact topics and the homes asking */}
      <h2 className="insight-heading">Leads</h2>
      <LeadInsights range={range} widgetId={widgetId} stats={stats} widgets={widgets} period={period} product={product} />

      {/* QuoteFlow companies only: the quote funnel, value, speed and sources */}
      {/* QuoteFlow is heat pumps only for now (solar: coming soon) */}
      {product !== "solar" && <QuoteInsights range={range} widgetId={widgetId} period={period} />}
    </div>
  );
}
